import {
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
  createContext,
  useCallback,
} from 'react'
import { isAfter, isBefore, format } from 'date-fns'

import { getRequest } from 'lib/service/fetchService'

import MaintenanceMessage from 'components/MaintenanceMessage'
import ThemeProvider from 'Providers/ThemeProvider'
import Page from 'components/Layout/Page'

import config from '../config'
import { BrowserRouter as Router } from 'react-router-dom'

interface MaintenanceDetails {
  startDateTime: string
  endDateTime: string
  message?: string
}

interface TSystemMaintanceContext {
  fetchMaintenanceWindow?: () => Promise<void>
  isInMaintenanceWindow: () => boolean
  startDateTime?: Date
  endDateTime?: Date
  message?: string
}

const SystemMaintanceContext = createContext<TSystemMaintanceContext>({
  isInMaintenanceWindow: () => false,
})

export function useSystemMaintenance() {
  return useContext(SystemMaintanceContext)
}

export default function SystemMaintanceProvider({
  children,
}: PropsWithChildren<Record<string, unknown>>) {
  const [systemMaintenanceState, setSystemMaintenanceState] = useState<{
    startDateTime?: Date
    endDateTime?: Date
    message: string
  }>()

  const fetchMaintenanceWindow = useCallback(async () => {
    try {
      const { body } = await getRequest<MaintenanceDetails>({
        url: `${config.FORMS_API_URL}/system-maintenance`,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      })
      if (!body) {
        setSystemMaintenanceState({
          message: '',
        })
        return
      }

      const { startDateTime, endDateTime, message } = body

      if (!startDateTime || !endDateTime) {
        setSystemMaintenanceState({
          message: '',
        })
        return
      }

      const startDate = new Date(startDateTime)
      const endDate = new Date(endDateTime)
      setSystemMaintenanceState({
        startDateTime: startDate,
        endDateTime: endDate,
        message:
          message ||
          `The system is currently unavailable, it will be back online at ${format(
            endDate,
            'h:mm b dd/MM/yyyy',
          )}`,
      })
    } catch (e) {
      setSystemMaintenanceState({
        message: 'The system is currently unavailable, please try again later',
      })
    }
  }, [])

  useEffect(() => {
    fetchMaintenanceWindow()
  }, [fetchMaintenanceWindow])

  const isInMaintenanceWindow = useCallback(() => {
    if (
      systemMaintenanceState?.startDateTime &&
      systemMaintenanceState.endDateTime
    ) {
      const now = new Date()
      return (
        isAfter(now, systemMaintenanceState?.startDateTime) &&
        isBefore(now, systemMaintenanceState.endDateTime)
      )
    }

    return false
  }, [
    systemMaintenanceState?.endDateTime,
    systemMaintenanceState?.startDateTime,
  ])

  if (isInMaintenanceWindow()) {
    return (
      <Router>
        <ThemeProvider>
          <Page>
            <MaintenanceMessage
              message={systemMaintenanceState?.message ?? ''}
            />
          </Page>
        </ThemeProvider>
      </Router>
    )
  }

  return (
    <SystemMaintanceContext.Provider
      value={{
        fetchMaintenanceWindow,
        isInMaintenanceWindow,
        ...systemMaintenanceState,
      }}
    >
      {children}
    </SystemMaintanceContext.Provider>
  )
}
